import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NewsletterSignUp from '../../common/Newsletter-sign';
import heroImage from '../../../assets/christmas25/tea1.webp';
import tea2Image from '../../../assets/christmas25/tea2.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  bronze:    '#8B5E3C',
  crimson:   '#8B1A1A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(520px, 82svh, 700px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(1.5rem, 3vw, 2.25rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,150,60,0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 1.4rem;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

const Notice = styled.div`
  background: rgba(201,150,60,0.08);
  border-left: 4px solid ${C.gold};
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${C.navy};
  font-weight: 600;
  margin-top: 1.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const ImgStrip = styled.div`
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 40vw, 420px);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
`;

const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${C.parchment};
  border: 1px solid rgba(201,150,60,0.2);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.28rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${C.navy};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.a`
  flex-shrink: 0;
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

const FestiveAfternoonTea = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>Festive Afternoon Tea at Belvoir Castle | Christmas 2026</title>
        <meta name="description" content="Indulge in a Festive Afternoon Tea at Belvoir Castle this Christmas — seasonal savouries, indulgent patisserie and fine teas in the elegant Aviary Tearoom, surrounded by the magic of Deck the Halls. Dates coming soon." />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/festive-afternoon-tea" />
        <meta property="og:title" content="Festive Afternoon Tea at Belvoir Castle | Christmas 2026" />
        <meta property="og:description" content="Seasonal savouries, indulgent patisserie and fine teas in the Aviary Tearoom — surrounded by the magic of Deck the Halls. Dates coming soon." />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/festive-afternoon-tea" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Festive Afternoon Tea — Christmas 2026 at Belvoir Castle" />
        <meta name="twitter:description" content="Seasonal patisserie, savouries and fine teas in the Aviary Tearoom during Deck the Halls. Dates coming soon." />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      <HeroWrap>
        <HeroImg src={heroImage} alt="Festive Afternoon Tea at Belvoir Castle" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>Aviary Tearoom, Belvoir Castle · Christmas 2026</HeroKicker>
          <HeroTitle>
            Festive <em>Afternoon Tea</em>
          </HeroTitle>
          <HeroStrap>
            Seasonal indulgence in our elegant tearooms, surrounded by the magic of Deck the Halls
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Dates Coming Soon</Pill>
            <Pill>Add-on to Castle Entry</Pill>
            <Pill>Aviary Tearoom</Pill>
          </BadgeRow>
        </HeroContent>
      </HeroWrap>

      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Experience</Kicker>
              <Divider />
              <SectionTitle>Christmas Indulgence<br /><em>in the Castle</em></SectionTitle>
              <SectionLead>
                Celebrate the season with a lavish Festive Afternoon Tea in the Aviary Tearoom at Belvoir Castle. A curated selection of seasonal savouries, delicate patisserie and freshly baked scones — served with fine teas in the warmth of our historic tearooms.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                Enjoy your afternoon tea surrounded by the magic of Deck the Halls — Belvoir Castle dressed in its full Christmas splendour, each grand room themed as a different carol. Pair it with your Castle entry for the perfect festive day out.
              </SectionLead>
              <Notice>
                Dates and booking for Festive Afternoon Tea 2026 are coming soon. Sign up below to be the first to know when tickets are available.
              </Notice>
              <MidCta style={{ marginTop: '1.5rem' }}>
                <MidCtaText>Sign up to be the first to hear about Festive Afternoon Tea 2026</MidCtaText>
                <MidCtaBtn href="#newsletter">Notify Me</MidCtaBtn>
              </MidCta>
            </div>
            <ImgStrip>
              <img src={tea2Image} alt="Festive Afternoon Tea at Belvoir Castle" />
            </ImgStrip>
          </TwoCols>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What to Expect</Kicker>
          <Divider />
          <SectionTitle>A Festive Tea <em>Like No Other</em></SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>🫖</FeatureIcon>
              <FeatureTitle>Fine Teas & Seasonal Drinks</FeatureTitle>
              <FeatureBody>A carefully chosen selection of fine teas, served with warmth and elegance in the beautiful surroundings of the Aviary Tearoom at Belvoir Castle.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🍰</FeatureIcon>
              <FeatureTitle>Festive Patisserie</FeatureTitle>
              <FeatureBody>Indulgent seasonal sweet treats — delicate patisserie crafted for Christmas, alongside freshly baked scones with clotted cream and preserves.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🥪</FeatureIcon>
              <FeatureTitle>Seasonal Savouries</FeatureTitle>
              <FeatureBody>A curated selection of seasonal savoury bites — crafted from the finest local and estate ingredients, perfectly balanced with the sweet course.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🏰</FeatureIcon>
              <FeatureTitle>Inside Deck the Halls</FeatureTitle>
              <FeatureBody>Your afternoon tea takes place inside Belvoir Castle during Deck the Halls — surrounded by fifteen grand rooms each dressed as a different Christmas carol.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon>🎄</FeatureIcon>
              <FeatureTitle $light>Add to Your Castle Visit</FeatureTitle>
              <FeatureBody $light>Book your Deck the Halls entry ticket and add Festive Afternoon Tea during checkout. Choose your time slot and make it a full, memorable Christmas day.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>

      <Section $bg={C.parchment} $border>
        <Inner>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <InfoValue>Coming Soon<br />2026</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Aviary Tearoom<br />Belvoir Castle</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Booking</InfoLabel>
              <InfoValue>Add-on to<br />Castle Entry</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Time Slot</InfoLabel>
              <InfoValue>Selected<br />at Checkout</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Family Saver</InfoLabel>
              <InfoValue>Available on<br />Castle Entry</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>Belvoir Castle<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      <div id="newsletter" style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>

      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Be the First <em>to Know</em>
          </CtaTitle>
          <CtaBody>
            Festive Afternoon Tea 2026 dates and booking are coming soon. Sign up to our newsletter to be the first to hear — or book your Deck the Halls entry now and add tea when it becomes available.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">
              Book Castle Entry
            </CtaBtn>
            <CtaSecondary to="/christmas/your-festive-visit">
              Plan Your Visit
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Christmas 2026 · Aviary Tearoom, Belvoir Castle · Dates Coming Soon
          </CtaMeta>
        </CtaInner>
      </CtaSection>
    </Page>
  );
};

export default FestiveAfternoonTea;
